import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import SectionWrapper from "../components/SectionWrapper";
import WhychooseUs from "../components/WhychooseUs";
import KeyStrengths from "../components/KeyStrengths";
import RatingStars from "../components/ui/RatingStars";
import { blogPosts, eventItems } from "../data/healthcareContent";
import { getAllTestimonials } from "../api/testimonialApi";
import { normalizeTestimonials } from "../utils/adminData";

const HomePage = () => {
  const images = [
    "/ImageAndVideo/image/careimg1.jpg",
    "/ImageAndVideo/image/careimg2.jpeg",
    "/ImageAndVideo/image/careimg3.jpeg",
    "/ImageAndVideo/image/careimg4.jpg",
    "/ImageAndVideo/image/careimg5.jpg",
  ];

  const loopImages = [...images, images[0]];

  const [testimonials, setTestimonials] = useState([]);
  const approvedTestimonials = useMemo(
    () => testimonials.filter((item) => item.status === "approved").slice(0, 3),
    [testimonials]
  );

  const latestBlogs = useMemo(() => blogPosts.slice(0, 4), []);
  const upcomingEvents = useMemo(() => eventItems.slice(0, 3), []);

  const [current, setCurrent] = useState(0);
  const [transition, setTransition] = useState(true);
  const [blogIndex, setBlogIndex] = useState(0);
  const [now, setNow] = useState(Date.now());

  const navigate = useNavigate();
  const [showConsultDropdown, setShowConsultDropdown] = useState(false);
  const closeTimeoutRef = useRef(null);

  const handleConsultEnter = () => {
    clearTimeout(closeTimeoutRef.current);
    setShowConsultDropdown(true);
  };

  const handleConsultLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setShowConsultDropdown(false);
    }, 150); // small delay so moving mouse into dropdown doesn't close it
  };

  const handleConsultSelect = (type) => {
    setShowConsultDropdown(false);

    navigate("/book-consultant", {
      state: {
        bookingType: type === "home-visit" ? "home" : "daily",
        fromHome: true,
      },
    });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((prev) => prev + 1);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (current === loopImages.length - 1) {
      const timer = setTimeout(() => {
        setTransition(false);
        setCurrent(0);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTransition(true);
          });
        });
      }, 700);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [current, loopImages.length]);

  useEffect(() => {
    const blogTimer = setInterval(() => {
      setBlogIndex((prev) => (prev + 1) % latestBlogs.length);
    }, 3200);
    const clockTimer = setInterval(() => setNow(Date.now()), 1000);

    return () => {
      clearInterval(blogTimer);
      clearInterval(clockTimer);
    };
  }, [latestBlogs.length]);

  useEffect(() => {
    let isMounted = true;

    const loadTestimonials = async () => {
      const response = await getAllTestimonials();
      const normalizedTestimonials = normalizeTestimonials(response);

      if (isMounted) {
        setTestimonials(normalizedTestimonials);
      }
    };

    loadTestimonials();

    return () => {
      isMounted = false;
    };
  }, []);

  const getCountdown = (dateValue) => {
    const diff = new Date(dateValue).getTime() - now;
    if (diff <= 0) return "Started";
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    return `${days}d ${hours}h`;
  };

  return (
    <main className="mt-12 text-gray-800 font-sans">
      <SectionWrapper
        className="relative bg-maroon text-white overflow-hidden lg:h-[95vh]"
        aria-labelledby="hero-heading"
      >
        <div className="absolute inset-0">
          <video
            className="w-full h-full object-cover opacity-50"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            disablePictureInPicture
            controls={false}
            controlsList="nodownload noplaybackrate nofullscreen"
            poster="/ImageAndVideo/Video/carev1-thumbnail.jpg"
          >
            <source src="/ImageAndVideo/Video/carev1.mp4" type="video/mp4" />
          </video>
        </div>

        <div className="relative z-10 grid md:grid-cols-2 items-center py-12 lg:px-6 lg:py-24 px-2">
          <div>
            <h1
              id="hero-heading"
              className="text-2xl md:text-5xl font-bold leading-tight mb-6"
            >
              Complete healthcare for senior citizens in India
            </h1>
            <p className="text-xl text-gray-100 mb-8 leading-relaxed">
              India’s NRI-Focused Eldercare Platform — Delivering primary
              preventive and assistive healthcare with compassion.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link
                to="/contact"
                className="bg-transparent text-gold text-center hover:bg-gold hover:text-maroon px-6 py-3 sm:px-7 sm:py-3.5 rounded-full font-semibold text-sm sm:text-base border-2 border-gold transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0"
              >
                Contact Us to Know More
              </Link>
              <div
                className="relative"
                onMouseEnter={handleConsultEnter}
                onMouseLeave={handleConsultLeave}
              >
                <button
                  type="button"
                  onClick={() => setShowConsultDropdown((prev) => !prev)}
                  className="w-full sm:w-auto bg-transparent text-gold text-center hover:bg-gold hover:text-maroon px-6 py-3 sm:px-7 sm:py-3.5 rounded-full font-semibold text-sm sm:text-base border-2 border-gold transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2"
                  aria-haspopup="true"
                  aria-expanded={showConsultDropdown}
                >
                  Book Doctor Consultation
                  <svg
                    className={`w-4 h-4 transition-transform duration-300 ${showConsultDropdown ? "rotate-180" : ""
                      }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showConsultDropdown && (
                  <div
                    className="absolute left-0 right-0 bottom-full mb-3 bg-white 
                rounded-2xl overflow-hidden z-[50] animate-fadeIn 
                border-[2px] border-gray-600 shadow-3xl"
                  >
                    <button
                      type="button"
                      onClick={() => handleConsultSelect("home-visit")}
                      className="w-full text-left px-5 py-4 hover:bg-cream transition-colors duration-200 flex items-start gap-3 border-b border-gray-100"
                      role="menuitem"
                    >
                      <span className="mt-0.5 text-maroon">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                      </span>
                      <span>
                        <span className="block font-semibold text-maroon text-sm">Home Visit</span>
                        {/* <span className="block text-xs text-gray-500 mt-0.5">
                          Book for Delhi NCR region at just ₹1100 / ₹500.
                        </span> */}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleConsultSelect("daily-consultant")}
                      className="w-full text-left px-5 py-4 hover:bg-cream transition-colors duration-200 flex items-start gap-3"
                      role="menuitem"
                    >
                      <span className="mt-0.5 text-maroon">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </span>
                      <span>
                        <span className="block font-semibold text-maroon text-sm">Tele - Consultation</span>
                        {/* <span className="block text-xs text-gray-500 mt-0.5">
                          Schedule regular checkup at just ₹1100 / ₹500.
                        </span> */}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="relative w-full flex flex-col items-center justify-center overflow-hidden py-6">
            <div className="relative w-full max-w-md md:max-w-lg overflow-hidden rounded-3xl shadow-lg">
              <div
                className={`flex ${transition ? "transition-transform duration-700 ease-in-out" : ""
                  }`}
                style={{ transform: `translateX(-${current * 100}%)` }}
              >
                {loopImages.map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt="slider-img"
                    className="w-full object-cover flex-shrink-0 rounded-3xl"
                    width={480}
                    height={320}
                  />
                ))}
              </div>
            </div>

            <div className="flex mt-4 space-x-2">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`w-5 h-5 rounded-full transition-all duration-300 ${current % images.length === i
                    ? "bg-white border-4 border-maroon"
                    : "bg-white opacity-60"
                    }`}
                ></button>
              ))}
            </div>
          </div>
        </div>
      </SectionWrapper>

      <KeyStrengths />
      <WhychooseUs />

      {approvedTestimonials.length > 0 ? (
        <SectionWrapper className="pt-0">
          <div className="rounded-2xl bg-white p-6 shadow-md">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-maroon">Testimonials</h2>
              <Link to="/testimonials" className="text-sm font-semibold text-maroon underline">
                View all
              </Link>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {approvedTestimonials.map((item) => (
                <article key={item.id} className="rounded-xl bg-cream p-4">
                  <div className="flex items-center gap-3">
                    <img src={item.photo} alt={item.fullName} className="h-11 w-11 rounded-full object-cover" />
                    <div>
                      <h3 className="font-semibold text-maroon">{item.fullName}</h3>
                      <p className="text-xs text-gray-500">{item.city}</p>
                    </div>
                  </div>
                  <div className="mt-2">
                    <RatingStars value={item.rating} readonly />
                  </div>
                  <p className="mt-2 text-sm text-gray-700">{item.message}</p>
                </article>
              ))}
            </div>
          </div>
        </SectionWrapper>
      ) : null}

      <SectionWrapper className="pt-0">
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="rounded-2xl bg-white p-6 shadow-md lg:col-span-2">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-maroon">Latest Blogs</h2>
              <Link to="/blogs" className="text-sm font-semibold text-maroon underline">
                Explore hub
              </Link>
            </div>
            <div className="overflow-hidden rounded-xl border border-gray-100">
              <motion.div
                key={latestBlogs[blogIndex].id}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.25 }}
                className="grid md:grid-cols-2"
              >
                <img
                  src={latestBlogs[blogIndex].image}
                  alt={latestBlogs[blogIndex].title}
                  className="h-48 w-full object-cover md:h-full"
                />
                <div className="p-5">
                  <span className="rounded-full bg-maroon/10 px-3 py-1 text-xs font-semibold text-maroon">
                    {latestBlogs[blogIndex].category}
                  </span>
                  <h3 className="mt-3 text-xl font-semibold text-maroon">{latestBlogs[blogIndex].title}</h3>
                  <p className="mt-2 text-sm text-gray-600">{latestBlogs[blogIndex].excerpt}</p>
                </div>
              </motion.div>
            </div>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-md">
            <h2 className="text-2xl font-bold text-maroon">Upcoming Events</h2>
            <div className="mt-4 space-y-3">
              {upcomingEvents.map((event) => (
                <div key={event.id} className="rounded-xl bg-cream p-3">
                  <p className="text-sm font-semibold text-maroon">{event.title}</p>
                  <p className="mt-1 text-xs text-gray-600">{event.venue}</p>
                  <p className="mt-1 text-xs font-semibold text-maroon">
                    Starts in {getCountdown(event.date)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </SectionWrapper>

      <SectionWrapper className="pt-0">
        <div className="rounded-2xl bg-gradient-to-r from-maroon to-[#2f5475] p-7 text-white shadow-lg flex flex-col md:flex-row md:items-center md:justify-between gap-5">
          <div>
            <h2 className="text-2xl font-bold">Book Doctor Consultation in Minutes</h2>
            <p className="mt-2 text-white/90">
              Multi-step consultation booking for clinic, online, and Delhi NCR home visits.
            </p>
          </div>
          <Link
            to="/book-consultant"
            className="rounded-full bg-white px-6 py-3 text-sm font-semibold text-maroon"
          >
            Book Consultation
          </Link>
        </div>
      </SectionWrapper>

      <SectionWrapper className="bg-maroon text-center text-white py-16">
        <h2 className="text-3xl font-bold mb-10">
          Ready to Ensure the Best Care for Your Loved Ones?
        </h2>
        <Link
          to="/contact"
          className="bg-gold text-maroon hover:bg-gold-light px-8 py-4 rounded-full font-semibold text-lg"
        >
          Contact Us to Know More
        </Link>
      </SectionWrapper>
    </main>
  );
};

export default HomePage;
